import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import {
  Link2, DollarSign, TrendingUp, Wallet, Loader2, Search, Copy, Check, Star,
  Sparkles, Clock, LinkIcon, Receipt, BadgePercent, Users, Calendar, Building2, X, ChevronRight, CalendarDays,
} from "lucide-react";

export default function AgentDashboard() {
  const { user, refreshUser } = useAuth();
  const [tab, setTab] = useState("links");
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    api.get("/agent/me").then((r) => setMe(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); refreshUser(); }, []); // eslint-disable-line

  const pending = (me?.status || user?.agent_status) !== "approved";

  return (
    <div className="min-h-screen bg-[#060810]">
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pt-10 pb-16">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <span className="font-mono-caption text-xs uppercase tracking-[0.2em] text-fuchsia-400">Agent Studio</span>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-white mt-1">{user?.name}</h1>
            {me?.handle && <p className="text-slate-400 text-sm mt-1">Your handle: <span className="text-cyan-300 font-semibold">@{me.handle}</span></p>}
          </div>
          <div className="glass rounded-2xl px-5 py-3 flex items-center gap-3" data-testid="agent-balance-card">
            <div className="w-10 h-10 rounded-xl bg-grad-cool flex items-center justify-center text-white"><Wallet className="w-5 h-5" /></div>
            <div>
              <p className="font-mono-caption text-[10px] uppercase tracking-widest text-cyan-300">Balance</p>
              <p className="font-heading text-xl font-extrabold text-gradient-cool leading-none">${(me?.balance ?? 0).toLocaleString()}</p>
            </div>
          </div>
        </div>

        {pending && (
          <div className="glass neon-border rounded-2xl p-5 mb-8 flex items-start gap-3" data-testid="agent-pending-banner">
            <Clock className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
            <div>
              <p className="font-heading font-semibold text-white">Your agent account is pending approval</p>
              <p className="text-slate-400 text-sm mt-1">An admin will review your application shortly. Once approved, you can generate tracking links and start earning commission.</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-[#232A4D]">
          {[
            { key: "links", label: "My Links", icon: Link2 },
            { key: "earnings", label: "Earnings", icon: Receipt },
          ].map((t) => (
            <button key={t.key} data-testid={`agent-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === t.key ? "border-cyan-400 text-cyan-400" : "border-transparent text-slate-400 hover:text-white"
              }`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>
        ) : pending ? (
          <div className="text-center py-16 glass rounded-2xl">
            <p className="text-slate-400 text-sm">Come back once your account is approved.</p>
          </div>
        ) : tab === "links" ? (
          <MyLinks handle={me?.handle} baseRate={me?.base_rate || 0} />
        ) : (
          <Earnings onChange={load} />
        )}
      </div>
      <Footer />
    </div>
  );
}

function MyLinks({ handle, baseRate }) {
  const [q, setQ] = useState("");
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);

  const search = (query) => {
    setLoading(true);
    api.get("/agent/tours", { params: { q: query || undefined } })
      .then((r) => setTours(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { search(""); }, []); // eslint-disable-line
  useEffect(() => {
    const t = setTimeout(() => search(q), 300);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line

  const linkFor = (id) => `${window.location.origin}/listing/${id}?agent=${handle}`;
  const copy = async (id) => {
    try {
      await navigator.clipboard.writeText(linkFor(id));
      setCopied(id);
      toast.success("Tracking link copied!");
      setTimeout(() => setCopied(null), 1800);
    } catch { toast.error("Could not copy"); }
  };

  return (
    <div>
      <div className="flex items-center gap-3 bg-[#0B0E1B] border border-[#1D2640] rounded-xl px-4 py-3 mb-6 focus-within:border-cyan-400/50 max-w-xl">
        <Search className="w-4 h-4 text-slate-500" />
        <input data-testid="agent-tour-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search tours you can promote…" className="bg-transparent text-white text-sm w-full outline-none" />
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-cyan-400" /></div>
      ) : tours.length === 0 ? (
        <div className="text-center py-16 glass rounded-2xl" data-testid="agent-no-tours">
          <LinkIcon className="w-9 h-9 text-slate-600 mx-auto mb-3" />
          <p className="font-heading text-lg text-white mb-1">No promotable tours found</p>
          <p className="text-slate-400 text-sm">Only tours whose hosts opted into the agent marketplace appear here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="agent-tours-list">
          {tours.map((t) => (
            <div key={t.id} data-testid={`agent-tour-${t.id}`} className="glass rounded-2xl p-4 flex gap-4">
              <img src={fileUrl(t.image_url)} alt="" className="w-28 h-28 rounded-xl object-cover shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <Link to={`/listing/${t.id}`} className="font-heading font-semibold text-white hover:text-cyan-300 truncate">{t.title}</Link>
                  {t.has_bonus && (
                    <span data-testid={`agent-bonus-${t.id}`} className="shrink-0 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-400/30 rounded-full px-2 py-0.5">
                      <Sparkles className="w-3 h-3" /> Bonus Boost
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                  <span>{t.island}</span>
                  <span className="flex items-center gap-1"><Star className="w-3 h-3 text-emerald-400 fill-emerald-400" /> {t.star_rating}</span>
                  <span className="text-[#FF3D57] font-semibold">${t.price}</span>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <span className="flex items-center gap-1 text-xs text-cyan-300 font-semibold"><BadgePercent className="w-3.5 h-3.5" /> You earn {(t.total_rate * 100).toFixed(1)}%</span>
                  {t.has_bonus && <span className="text-[11px] text-slate-500">({(baseRate * 100).toFixed(1)}% base + {(t.bonus_rate * 100).toFixed(1)}% bonus)</span>}
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <input readOnly value={linkFor(t.id)} data-testid={`agent-link-${t.id}`}
                    className="flex-1 bg-[#0B0E1B] border border-[#1D2640] rounded-lg px-3 py-2 text-xs text-slate-300 outline-none truncate" />
                  <button onClick={() => copy(t.id)} data-testid={`agent-copy-${t.id}`}
                    className="shrink-0 btn-neon rounded-lg px-3 py-2 text-xs font-semibold flex items-center gap-1">
                    {copied === t.id ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Earnings({ onChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [trip, setTrip] = useState(null);        // ledger row -> booking detail
  const [providerId, setProviderId] = useState(null); // -> provider performance record
  const ledgerRef = React.useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/agent/earnings").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const requestPayout = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/agent/request-payout");
      toast.success(`Payout of $${res.amount.toLocaleString()} requested!`);
      load(); onChange && onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const scrollToLedger = () => {
    ledgerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    ledgerRef.current?.classList.add("ring-2", "ring-cyan-400/60");
    setTimeout(() => ledgerRef.current?.classList.remove("ring-2", "ring-cyan-400/60"), 1500);
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-cyan-400" /></div>;
  const d = data || {};

  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <Stat icon={CalendarDays} color="fuchsia" label="Earnings this month" value={`$${(d.this_month ?? 0).toLocaleString()}`} testid="agent-this-month" onClick={scrollToLedger} />
        <Stat icon={TrendingUp} color="cyan" label="Total earned" value={`$${(d.total_earned ?? 0).toLocaleString()}`} testid="agent-total-earned" onClick={scrollToLedger} />
        <Stat icon={Wallet} color="emerald" label="Available balance" value={`$${(d.balance ?? 0).toLocaleString()}`} testid="agent-available" highlight onClick={scrollToLedger} />
        <Stat icon={Clock} color="violet" label="Pending payout" value={`$${(d.requested ?? 0).toLocaleString()}`} testid="agent-requested" onClick={scrollToLedger} />
        <Stat icon={DollarSign} color="coral" label="Paid out" value={`$${(d.paid_out ?? 0).toLocaleString()}`} testid="agent-paidout" onClick={scrollToLedger} />
      </div>

      <div className="glass rounded-2xl p-5 mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-heading font-semibold text-white">Request a payout</p>
          <p className="text-slate-400 text-sm mt-1">Move your available balance to <span className="text-violet-300">Pending payout</span> for our team to transfer. Available once your balance reaches ${d.payout_threshold ?? 50}.</p>
        </div>
        <button onClick={requestPayout} disabled={busy || !d.can_request} data-testid="agent-request-payout-btn"
          className="btn-neon rounded-full px-6 py-2.5 font-semibold text-sm flex items-center gap-2 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wallet className="w-4 h-4" />} Request payout
        </button>
      </div>

      {/* Providers performance */}
      {(d.providers || []).length > 0 && (
        <div className="mb-8">
          <h3 className="font-heading font-semibold text-white mb-3">Performance by provider</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="agent-providers">
            {d.providers.map((p) => (
              <button key={p.provider_id} data-testid={`agent-provider-${p.provider_id}`} onClick={() => setProviderId(p.provider_id)}
                className="glass rounded-2xl p-4 text-left transition-all hover:ring-1 hover:ring-cyan-400/40 hover:-translate-y-0.5 group">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 font-heading font-semibold text-white group-hover:text-cyan-300 transition-colors"><Building2 className="w-4 h-4 text-cyan-400" /> {p.provider_name}</span>
                  <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-cyan-400 group-hover:translate-x-0.5 transition-all" />
                </div>
                <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                  <span>{p.bookings} booking{p.bookings === 1 ? "" : "s"}</span>
                  <span className="text-emerald-400 font-semibold">${p.commission.toLocaleString()} earned</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <h3 ref={ledgerRef} className="font-heading font-semibold text-white mb-3 scroll-mt-24 rounded transition-all">Commission ledger</h3>
      <div data-testid="agent-ledger">
      {(d.entries || []).length === 0 ? (
        <div className="text-center py-12 glass rounded-2xl" data-testid="agent-ledger-empty">
          <p className="text-slate-400 text-sm">No commissions yet. Share your tracking links to start earning.</p>
        </div>
      ) : (
        <div className="glass rounded-2xl overflow-hidden overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="text-left text-xs text-slate-400 border-b border-[#1D2640]">
                <th className="px-5 py-3 font-medium">Trip</th>
                <th className="px-5 py-3 font-medium">Provider</th>
                <th className="px-5 py-3 font-medium">Date</th>
                <th className="px-5 py-3 font-medium text-right">Value</th>
                <th className="px-5 py-3 font-medium text-right">Commission</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {d.entries.map((e) => (
                <tr key={e.booking_id} data-testid={`agent-ledger-${e.booking_id}`} onClick={() => setTrip(e)}
                  className="border-b border-[#1D2640]/50 cursor-pointer hover:bg-cyan-400/5 transition-colors group">
                  <td className="px-5 py-3 text-white group-hover:text-cyan-300 transition-colors">{e.tour}</td>
                  <td className="px-5 py-3">
                    <button data-testid={`ledger-provider-${e.booking_id}`}
                      onClick={(ev) => { ev.stopPropagation(); setProviderId(e.provider_id); }}
                      className="text-fuchsia-300 hover:text-fuchsia-200 hover:underline underline-offset-2 transition-colors">
                      {e.provider_name}
                    </button>
                  </td>
                  <td className="px-5 py-3 text-slate-400">{e.date}</td>
                  <td className="px-5 py-3 text-right text-slate-300">${(e.gross ?? 0).toLocaleString()}</td>
                  <td className="px-5 py-3 text-right text-emerald-400 font-semibold">${(e.commission ?? 0).toLocaleString()}</td>
                  <td className="px-5 py-3 text-right"><ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-cyan-400 group-hover:translate-x-0.5 transition-all inline" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>

      {(d.payouts || []).length > 0 && (
        <>
          <h3 className="font-heading font-semibold text-white mt-8 mb-3">Payout history</h3>
          <div className="glass rounded-2xl divide-y divide-[#1D2640]/60" data-testid="agent-payout-history">
            {d.payouts.map((p) => (
              <div key={p.id} className="flex items-center justify-between px-5 py-3 text-sm">
                <span className="text-slate-300">${(p.amount ?? 0).toLocaleString()}</span>
                <span className="text-xs text-slate-500">{(p.requested_at || "").slice(0, 10)}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full border ${p.status === "paid" ? "text-emerald-400 border-emerald-400/30" : "text-amber-400 border-amber-400/30"}`}>
                  {p.status === "paid" ? "Paid" : "Pending payout"}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {trip && <TripDetailModal trip={trip} onClose={() => setTrip(null)} onProvider={(pid) => { setTrip(null); setProviderId(pid); }} />}
      {providerId && <ProviderRecordModal providerId={providerId} onClose={() => setProviderId(null)} onTrip={(t) => { setProviderId(null); setTrip(t); }} />}
    </div>
  );
}

function Row({ label, value, className }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-[#1D2640]/50 last:border-0">
      <span className="text-xs text-slate-400">{label}</span>
      <span className={`text-sm font-medium ${className || "text-white"}`}>{value}</span>
    </div>
  );
}

function TripDetailModal({ trip, onClose, onProvider }) {
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div className="glass neon-border rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="trip-detail-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <span className="font-mono-caption text-[10px] uppercase tracking-widest text-cyan-300">Booking detail</span>
            <h2 className="font-heading text-lg font-bold text-white mt-1">{trip.tour}</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-0.5">
          <Row label="Provider" value={
            <button onClick={() => onProvider(trip.provider_id)} className="text-fuchsia-300 hover:underline flex items-center gap-1">
              <Building2 className="w-3.5 h-3.5" /> {trip.provider_name}
            </button>
          } />
          <Row label="Traveler" value={trip.traveler_name || "—"} />
          <Row label="Date" value={<span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-slate-500" />{trip.date} {trip.time || ""}</span>} />
          <Row label="Guests" value={<span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-slate-500" />{trip.guests}</span>} />
          <Row label="Status" value={<span className="capitalize text-emerald-400">{trip.status}</span>} />
          <Row label="Booking value" value={`$${(trip.gross ?? 0).toLocaleString()}`} />
          <Row label="Your rate" value={`${((trip.rate ?? 0) * 100).toFixed(1)}%`} />
          <Row label="Your commission" value={`$${(trip.commission ?? 0).toLocaleString()}`} className="text-emerald-400 font-bold" />
        </div>
      </div>
    </div>
  );
}

function ProviderRecordModal({ providerId, onClose, onTrip }) {
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/agent/providers/${providerId}`).then((r) => setRec(r.data)).catch(() => toast.error("Could not load provider record")).finally(() => setLoading(false));
  }, [providerId]);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div className="glass neon-border rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="provider-record-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <span className="font-mono-caption text-[10px] uppercase tracking-widest text-fuchsia-300">Provider record</span>
            <h2 className="font-heading text-lg font-bold text-white mt-1 flex items-center gap-2"><Building2 className="w-5 h-5 text-cyan-400" /> {rec?.provider_name || "Provider"}</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>
        ) : rec ? (
          <>
            <div className="grid grid-cols-3 gap-3 mb-5">
              <div className="glass rounded-xl p-3 text-center"><p className="text-[10px] uppercase tracking-wide text-slate-400">Bookings</p><p className="font-heading text-xl font-bold text-white mt-1">{rec.bookings}</p></div>
              <div className="glass rounded-xl p-3 text-center"><p className="text-[10px] uppercase tracking-wide text-slate-400">Sales value</p><p className="font-heading text-xl font-bold text-white mt-1">${rec.gross.toLocaleString()}</p></div>
              <div className="glass rounded-xl p-3 text-center neon-border"><p className="text-[10px] uppercase tracking-wide text-cyan-300">You earned</p><p className="font-heading text-xl font-bold neon-text-cyan mt-1">${rec.commission.toLocaleString()}</p></div>
            </div>
            <p className="text-xs text-slate-400 mb-2">Trips referred to this provider</p>
            <div className="space-y-2">
              {rec.trips.map((t) => (
                <button key={t.booking_id} onClick={() => onTrip(t)} data-testid={`provider-trip-${t.booking_id}`}
                  className="w-full glass rounded-xl px-4 py-3 flex items-center justify-between text-left transition-all hover:ring-1 hover:ring-cyan-400/40 group">
                  <div>
                    <p className="text-sm text-white group-hover:text-cyan-300 transition-colors">{t.tour}</p>
                    <p className="text-xs text-slate-500">{t.date} · {t.guests} guest{t.guests === 1 ? "" : "s"}</p>
                  </div>
                  <span className="text-emerald-400 font-semibold text-sm">${(t.commission ?? 0).toLocaleString()}</span>
                </button>
              ))}
            </div>
          </>
        ) : (
          <p className="text-slate-400 text-sm py-8 text-center">No record found.</p>
        )}
      </div>
    </div>
  );
}

const COLORS = { cyan: "text-cyan-400", emerald: "text-emerald-400", violet: "text-violet-400", coral: "text-[#FF5E7E]", fuchsia: "text-fuchsia-400" };
function Stat({ icon: Icon, color, label, value, testid, highlight, onClick }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      className={`glass rounded-2xl p-5 text-left w-full transition-all hover:-translate-y-0.5 hover:ring-1 hover:ring-cyan-400/40 ${highlight ? "neon-border" : ""}`}>
      <div className={`w-10 h-10 rounded-xl bg-[#0B0E1B] border border-[#1D2640] flex items-center justify-center mb-3 ${COLORS[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-xs text-slate-400">{label}</p>
      <p className={`font-heading text-2xl font-bold mt-1 ${highlight ? "neon-text-cyan" : "text-white"}`}>{value}</p>
    </button>
  );
}
